import { DroitsImagePanneau } from './DroitsImagePanneau.js';
import { Badge, Button, IconButton, Input, Select } from '@artosera/ui';
import { TYPES_MEDIA, type TypeMedia } from '@artosera/shared';
import { ImagePlus, Trash2 } from 'lucide-react';
import type { DragEvent, JSX } from 'react';
import { useRef, useState } from 'react';
import { estApiError } from '../../../api/errors.js';
import { useTranslation } from 'react-i18next';
import {
  useApercuMedia,
  useDeposerMedia,
  useModifierMedia,
  useSupprimerMedia,
} from '../../../api/medias.js';
import type { MediaDeLOeuvre } from '../../../api/oeuvres.js';
import { useOrdonnerMediasOeuvre } from '../../../api/oeuvres.js';
import styles from './Tabs.module.css';

export interface MediasTabProps {
  galerieSlug: string;
  oeuvreId: string;
  medias: readonly MediaDeLOeuvre[];
  mediaPrincipalId: string | null;
  /** Droit d'écriture (`media:write`) : sans lui, aperçu seul (ARTO-53, § 4). */
  peutEcrire: boolean;
  peutModifierDroits?: boolean;
}

/**
 * Médias (3.3, écran 2) : dépose, ordre par glisser, légende bilingue,
 * suppression douce. Sans `media:write`, la zone de dépôt, les champs de
 * métadonnées et Enregistrer/Supprimer n'existent pas — seul l'aperçu reste
 * (audit du 23 septembre 2026, § 1 ; ARTO-53, § 4).
 */
export function MediasTab({
  galerieSlug,
  oeuvreId,
  medias,
  mediaPrincipalId,
  peutEcrire,
  peutModifierDroits = false,
}: MediasTabProps): JSX.Element {
  const { t } = useTranslation(['regie', 'oeuvres', 'commun']);
  const ordonnes = [...medias].sort((a, b) => a.ordre - b.ordre);

  const ordonner = useOrdonnerMediasOeuvre(galerieSlug);
  const deposer = useDeposerMedia(galerieSlug);
  const fichierRef = useRef<HTMLInputElement>(null);
  const [survoleDepot, setSurvoleDepot] = useState(false);
  const [glisseId, setGlisseId] = useState<string | null>(null);

  function deposerFichier(fichier: File): void {
    deposer.mutate({ fichier, rattachementType: 'artwork', rattachementId: oeuvreId });
  }

  // Un fichier refusé (type, poids, signature) dit pourquoi, comme dans le
  // coffre : l'erreur reste affichée jusqu'au prochain dépôt.
  const erreurDepot = deposer.isError
    ? estApiError(deposer.error)
      ? deposer.error.message
      : t('commun:erreurReseau')
    : null;

  function surDeposeGlobal(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setSurvoleDepot(false);
    const fichier = event.dataTransfer.files[0];
    if (fichier) deposerFichier(fichier);
  }

  function deplacer(cibleId: string): void {
    if (!glisseId || glisseId === cibleId) return;
    const ids = ordonnes.map((media) => media.id);
    const depart = ids.indexOf(glisseId);
    const arrivee = ids.indexOf(cibleId);
    if (depart === -1 || arrivee === -1) return;
    ids.splice(arrivee, 0, ...ids.splice(depart, 1));
    ordonner.mutate({ id: oeuvreId, medias: ids });
  }

  return (
    <div className={styles.mediasPage} data-ui="artwork-media">
      {peutEcrire ? (
        <div
          className={styles.zoneDepot}
          data-survole={survoleDepot ? 'true' : 'false'}
          onDragOver={(event) => {
            event.preventDefault();
            setSurvoleDepot(true);
          }}
          onDragLeave={() => setSurvoleDepot(false)}
          onDrop={surDeposeGlobal}
        >
          <ImagePlus size={24} strokeWidth={1.5} aria-hidden />
          <p>{t('regie:oeuvres.medias.glisserDeposer')}</p>
          <Button
            variant="secondary"
            size="sm"
            id="media-depot"
            onClick={() => fichierRef.current?.click()}
            loading={deposer.isPending}
          >
            {t('regie:oeuvres.medias.deposer')}
          </Button>
          <input
            ref={fichierRef}
            type="file"
            accept="image/*"
            aria-label={t('regie:oeuvres.medias.deposer')}
            hidden
            onChange={(event) => {
              const fichier = event.target.files?.[0];
              if (fichier) deposerFichier(fichier);
              event.target.value = '';
            }}
          />
        </div>
      ) : null}

      {erreurDepot ? (
        <p className={styles.erreur} role="alert">
          {erreurDepot}
        </p>
      ) : null}

      {ordonnes.length === 0 ? (
        <p className={styles.aide}>{t('regie:oeuvres.medias.aucunMedia')}</p>
      ) : (
        <>
          {peutEcrire ? <p className={styles.aide}>{t('regie:oeuvres.medias.ordreAide')}</p> : null}
          <div className={styles.listeMedias}>
            {ordonnes.map((media) => (
              <MediaCarte
                key={media.id}
                galerieSlug={galerieSlug}
                media={media}
                estPrincipal={media.id === mediaPrincipalId}
                peutModifierDroits={peutModifierDroits}
                peutEcrire={peutEcrire}
                survole={glisseId !== null && glisseId !== media.id}
                onDragStart={() => setGlisseId(media.id)}
                onDragEnd={() => setGlisseId(null)}
                onDropSur={() => deplacer(media.id)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

interface MediaCarteProps {
  galerieSlug: string;
  media: MediaDeLOeuvre;
  estPrincipal: boolean;
  peutEcrire: boolean;
  peutModifierDroits: boolean;
  survole: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDropSur: () => void;
}

function MediaCarte({
  galerieSlug,
  media,
  estPrincipal,
  peutEcrire,
  peutModifierDroits,
  survole,
  onDragStart,
  onDragEnd,
  onDropSur,
}: MediaCarteProps): JSX.Element {
  const { t } = useTranslation(['regie', 'oeuvres', 'commun']);
  const apercu = useApercuMedia(galerieSlug, media.id);
  const modifier = useModifierMedia(galerieSlug);
  const supprimer = useSupprimerMedia(galerieSlug);

  const [type, setType] = useState<TypeMedia>(media.type as TypeMedia);
  const [credit, setCredit] = useState(media.credit ?? '');
  const [legendeFr, setLegendeFr] = useState(media.legende?.fr ?? '');
  const [legendeEn, setLegendeEn] = useState(media.legende?.en ?? '');

  return (
    <div
      className={styles.ligneMedia}
      data-ui="artwork-media-row"
      data-survole={survole ? 'true' : 'false'}
      draggable={peutEcrire}
      onDragStart={peutEcrire ? onDragStart : undefined}
      onDragEnd={peutEcrire ? onDragEnd : undefined}
      onDragOver={peutEcrire ? (event) => event.preventDefault() : undefined}
      onDrop={peutEcrire ? onDropSur : undefined}
    >
      {/* Aperçu : l'image seule, sans champ à proximité (audit du 23
          septembre, § 1 — « séparer aperçu et édition des métadonnées »). */}
      <div className={styles.apercuMedia} data-ui="artwork-media-preview">
        {apercu.data ? (
          <img className={styles.vignetteMedia} src={apercu.data} alt="" />
        ) : (
          <div className={styles.vignetteMedia} aria-hidden />
        )}
      </div>

      {/* Édition des métadonnées : sa propre colonne, avec assez de largeur
          pour lire crédit et légendes sans les tasser. Sans `media:write`,
          consultation seule — aucun champ, aucun bouton (ARTO-53, § 4). */}
      <div className={styles.metadonneesMedia}>
        <DroitsImagePanneau
          key={JSON.stringify(media.droits)}
          galerieSlug={galerieSlug}
          media={media}
          peutEcrire={peutModifierDroits}
        />
        {estPrincipal ? (
          <Badge tone="accent" className={styles.principalBadge}>
            {t('regie:oeuvres.medias.definirPrincipal')}
          </Badge>
        ) : null}

        {peutEcrire ? (
          <>
            <div className={styles.champsMedia}>
              <Select
                aria-label={t('regie:oeuvres.medias.typeVue')}
                value={type}
                onValueChange={(valeur) => setType(valeur as TypeMedia)}
                options={TYPES_MEDIA.map((code) => ({
                  value: code,
                  label: t(`oeuvres:medias.${code}`),
                }))}
              />
              <Input
                id={estPrincipal ? 'media-credit-principal' : undefined}
                aria-label={t('regie:oeuvres.medias.credit')}
                placeholder={t('regie:oeuvres.medias.credit')}
                value={credit}
                onChange={(event) => setCredit(event.target.value)}
              />
              <Input
                aria-label={t('regie:oeuvres.medias.legendeFr')}
                placeholder={t('regie:oeuvres.medias.legendeFr')}
                value={legendeFr}
                onChange={(event) => setLegendeFr(event.target.value)}
              />
              <Input
                aria-label={t('regie:oeuvres.medias.legendeEn')}
                placeholder={t('regie:oeuvres.medias.legendeEn')}
                value={legendeEn}
                onChange={(event) => setLegendeEn(event.target.value)}
              />
            </div>

            {modifier.isError || supprimer.isError ? (
              <p className={styles.erreur} role="alert">
                {(() => {
                  const cause = modifier.isError ? modifier.error : supprimer.error;
                  return estApiError(cause) ? cause.message : t('commun:erreurReseau');
                })()}
              </p>
            ) : null}

            <div className={styles.cases}>
              <Button
                variant="secondary"
                size="sm"
                loading={modifier.isPending}
                onClick={() =>
                  modifier.mutate({
                    id: media.id,
                    corps: {
                      type,
                      credit: credit || null,
                      legende: { fr: legendeFr, en: legendeEn },
                    },
                  })
                }
              >
                {t('commun:actions.enregistrer')}
              </Button>
              <IconButton
                variant="outline"
                icon={<Trash2 aria-hidden />}
                aria-label={t('regie:oeuvres.medias.supprimer')}
                size="sm"
                onClick={() => {
                  if (window.confirm(t('regie:oeuvres.medias.supprimer')))
                    supprimer.mutate(media.id);
                }}
              />
            </div>
          </>
        ) : (
          <dl className={styles.donneesGrille}>
            <div>
              <dt>{t('regie:oeuvres.medias.typeVue')}</dt>
              <dd>{t(`oeuvres:medias.${media.type}`)}</dd>
            </div>
            {media.credit ? (
              <div>
                <dt>{t('regie:oeuvres.medias.credit')}</dt>
                <dd>{media.credit}</dd>
              </div>
            ) : null}
            {media.legende?.fr ? (
              <div>
                <dt>{t('regie:oeuvres.medias.legendeFr')}</dt>
                <dd>{media.legende.fr}</dd>
              </div>
            ) : null}
            {media.legende?.en ? (
              <div>
                <dt>{t('regie:oeuvres.medias.legendeEn')}</dt>
                <dd>{media.legende.en}</dd>
              </div>
            ) : null}
          </dl>
        )}
      </div>
    </div>
  );
}
